import React, { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useExecutives } from "@/context/ExecutivesContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Pencil, KeyRound, Loader2, Shield, Users, Trash2, Power, PowerOff, Lock, LockOpen } from "lucide-react";

const ROLES = [
  { value: "executive", label: "Sales Executive" },
  { value: "sales_manager", label: "Sales Manager" },
  { value: "management", label: "Management" },
];
const ROLE_LABEL = ROLES.reduce((a, r) => ((a[r.value] = r.label), a), {});
const ROLE_STYLE = {
  management: "bg-blue-50 text-blue-800 border-blue-200",
  sales_manager: "bg-violet-50 text-violet-800 border-violet-200",
  executive: "bg-slate-100 text-slate-700 border-slate-200",
};

function UserDialog({ open, onOpenChange, editUser, onSaved }) {
  const editing = Boolean(editUser);
  const [form, setForm] = useState({ name: "", email: "", role: "executive", password: "", require_change: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      if (editUser) {
        setForm({ name: editUser.name, email: editUser.email, role: editUser.role, password: "", require_change: true });
      } else {
        setForm({ name: "", email: "", role: "executive", password: "", require_change: true });
      }
    }
  }, [open, editUser]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/users/${editUser.id}`, {
          name: form.name,
          email: form.email,
          role: form.role,
        });
        toast.success("User updated");
      } else {
        await api.post("/admin/users", {
          name: form.name,
          email: form.email,
          role: form.role,
          password: form.password,
          require_change: form.require_change,
        });
        toast.success("User created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit User" : "Create User"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update the user's name, email, or role." : "Add a new staff account with an initial password."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Full Name</Label>
            <Input required data-testid="user-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g., Priya Nair" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" required data-testid="user-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@tamannahotels.com" />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!editing && (
            <>
              <div className="space-y-2">
                <Label>Initial Password</Label>
                <Input required data-testid="user-password-input" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 8 chars, 1 letter & 1 number" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="require_change" checked={form.require_change} onCheckedChange={(v) => set("require_change", Boolean(v))} data-testid="user-require-change" />
                <Label htmlFor="require_change" className="text-sm font-normal cursor-pointer">
                  Require password change on first login
                </Label>
              </div>
            </>
          )}
          {error && <p data-testid="user-dialog-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="user-dialog-submit" className="bg-[#1E3A8A] hover:bg-[#172554]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Create User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ open, onOpenChange, targetUser }) {
  const [password, setPassword] = useState("");
  const [requireChange, setRequireChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setPassword("");
      setRequireChange(true);
      setError("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post(`/admin/users/${targetUser.id}/password`, {
        new_password: password,
        require_change: requireChange,
      });
      toast.success(`Password reset for ${targetUser.name}`);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Set Password</DialogTitle>
          <DialogDescription>
            Set a new password for <span className="font-semibold">{targetUser?.name}</span>. No current password needed.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>New Password</Label>
            <Input required data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 chars, 1 letter & 1 number" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="req_change_reset" checked={requireChange} onCheckedChange={(v) => setRequireChange(Boolean(v))} data-testid="reset-require-change" />
            <Label htmlFor="req_change_reset" className="text-sm font-normal cursor-pointer">
              Require change on next login
            </Label>
          </div>
          {error && <p data-testid="password-dialog-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="password-dialog-submit" className="bg-[#1E3A8A] hover:bg-[#172554]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPanel() {
  const { refresh: refreshExecutives } = useExecutives();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDialog, setUserDialog] = useState({ open: false, user: null });
  const [pwDialog, setPwDialog] = useState({ open: false, user: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSaved = () => {
    load();
    refreshExecutives();
  };

  const toggleStatus = async (u) => {
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/status`, { is_active: !u.is_active });
      toast.success(u.is_active ? `${u.name} deactivated` : `${u.name} activated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const unlock = async (u) => {
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/unlock`);
      toast.success(`${u.name} unlocked`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const confirmDelete = async () => {
    const u = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="admin-panel">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="h-4 w-4 text-[#1E3A8A]" /> User Management
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">Create, edit and reset passwords for staff accounts</p>
        </div>
        <Button onClick={() => setUserDialog({ open: true, user: null })} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="create-user-button">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add User
        </Button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium text-slate-900 flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-slate-300" />
                  {u.name}
                </TableCell>
                <TableCell className="text-slate-600 font-mono text-xs">{u.email}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={ROLE_STYLE[u.role]}>{ROLE_LABEL[u.role]}</Badge>
                </TableCell>
                <TableCell>
                  {u.is_locked ? (
                    <span data-testid={`user-locked-${u.id}`} className="inline-flex items-center gap-1.5 text-xs text-rose-600 font-medium">
                      <Lock className="h-3 w-3" /> Locked
                    </span>
                  ) : u.is_active === false ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Inactive
                    </span>
                  ) : u.must_change_password ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Pending first-login
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" className="h-8 text-slate-600 hover:text-[#1E3A8A]" data-testid={`edit-user-${u.id}`} onClick={() => setUserDialog({ open: true, user: u })}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 text-slate-600 hover:text-[#1E3A8A]" data-testid={`reset-password-${u.id}`} onClick={() => setPwDialog({ open: true, user: u })}>
                    <KeyRound className="h-3.5 w-3.5 mr-1" /> Password
                  </Button>
                  {u.is_locked && (
                    <Button variant="ghost" size="sm" disabled={busyId === u.id} className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`unlock-user-${u.id}`} onClick={() => unlock(u)}>
                      <LockOpen className="h-3.5 w-3.5 mr-1" /> Unlock
                    </Button>
                  )}
                  {u.id !== me.id && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busyId === u.id}
                        className={`h-8 ${u.is_active === false ? "text-emerald-600 hover:text-emerald-700" : "text-amber-600 hover:text-amber-700"}`}
                        data-testid={`toggle-status-${u.id}`}
                        onClick={() => toggleStatus(u)}
                      >
                        {u.is_active === false ? (
                          <><Power className="h-3.5 w-3.5 mr-1" /> Activate</>
                        ) : (
                          <><PowerOff className="h-3.5 w-3.5 mr-1" /> Deactivate</>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => setDeleteTarget(u)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <UserDialog open={userDialog.open} onOpenChange={(o) => setUserDialog((s) => ({ ...s, open: o }))} editUser={userDialog.user} onSaved={onSaved} />
      {pwDialog.user && (
        <PasswordDialog open={pwDialog.open} onOpenChange={(o) => setPwDialog((s) => ({ ...s, open: o }))} targetUser={pwDialog.user} />
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-semibold">{deleteTarget?.name}</span> ({deleteTarget?.email}).
              Executives with assigned leads cannot be deleted — reassign their leads or deactivate the account instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-user-confirm" className="bg-rose-600 hover:bg-rose-700">
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
